import type { ParseConfig, ParseResult } from "papaparse";

const papaparseLoader = () => import("papaparse");

type CsvDatum<DynamicTyping extends ParseConfig["dynamicTyping"] = true> =
  DynamicTyping extends false | undefined
    ? string
    : string | number | boolean | Date | null;
export type CsvRow<DynamicTyping extends ParseConfig["dynamicTyping"] = true> =
  CsvDatum<DynamicTyping>[];
export type CsvRecord<
  DynamicTyping extends ParseConfig["dynamicTyping"] = true,
> = Partial<Record<string | symbol, CsvDatum<DynamicTyping>>>;

/**
 * This wrapper function exists for 2 reasons:
 *
 * First, it asynchronously imports the papaparse library so it is not
 * downloaded until it is needed (the library is quite large).
 *
 * Second, it applies a patch to a bug in the papaparse library with-respect-to
 * parsing headers.
 *
 * Headers are required to be unique and this is enforced by appending `_2` to
 * the end of duplicate header names. However, these suffixes are appended prior
 * to reconstruction of cells that contain commas.
 *
 * Example:
 * "Column,1","Column,2"
 * one,two
 *
 * Expected:
 * {
 *   "Column,1": "one",
 *   "Column,2": "two"
 * }
 *
 * Actual:
 * {
 *   "Column,1": "one",
 *   "Column_2,2": "two"
 * }
 */

export async function parse<
  Header extends ParseConfig["header"] = false,
  DynamicTyping extends ParseConfig["dynamicTyping"] = undefined,
>(
  content: File | string,
  config: ParseConfig & { header?: Header; dynamicTyping?: DynamicTyping } = {},
): Promise<
  ParseResult<
    Header extends true ? CsvRecord<DynamicTyping> : CsvRow<DynamicTyping>
  >
> {
  const { parse: papaparse } = await papaparseLoader();

  const patchedConfig: ParseConfig = {
    ...config,
    // use standard non-header parsing to avoid the bug described above
    header: false,
  };

  const results =
    typeof content === "string"
      ? papaparse<CsvRow<DynamicTyping>>(content, patchedConfig)
      : await new Promise<ParseResult<CsvRow<DynamicTyping>>>(
          (complete, error) =>
            papaparse<CsvRow<DynamicTyping>>(content, {
              ...patchedConfig,
              complete,
              error,
            }),
        );

  // if headers were requested, we must reconstruct the objects manually
  if (config.header) {
    const fields = (results.data.shift() ?? []).map(String);
    const data = results.data.map((row) =>
      Object.fromEntries(
        fields.map((field, index) => [
          field,
          row[index] ?? (config.dynamicTyping ? null : ""),
        ]),
      ),
    );
    return {
      ...results,
      data,
      meta: {
        ...results.meta,
        fields,
      },
    } as any;
  }

  return results as any;
}
